/**
 * Simulation (rollout) for MCTS
 * Plays random moves until terminal state
 */

import { GameState } from "../../types";
import { MCTSNode, MoveInfo } from "./MCTSNode";
import { cloneFullGameState } from "../../MCTS_v2/utils/MCTSUtils";

export class Simulation {
  protected maxSimulationDepth: number = 50; // Prevent infinite games

  /**
   * Run random simulation from given state to terminal position
   * Returns 1 for win, 0 for loss, 0.5 for draw (from perspective of starting player)
   */
  simulate(gameState: GameState, startingPlayer: boolean): number {
    let currentState = this.deepCopyGameState(gameState);
    let depth = 0;

    while (!this.isTerminal(currentState) && depth < this.maxSimulationDepth) {
      // Get all legal moves
      const moves = MCTSNode.getAllMoves(currentState);

      if (moves.length === 0) {
        // Stalemate
        return 0.5;
      }

      // Select random move
      const randomMove = moves[Math.floor(Math.random() * moves.length)];

      // Apply move
      const nextState = this.applyMove(currentState, randomMove);
      if (!nextState) {
        // Invalid move, treat as draw
        return 0.5;
      }
      currentState = nextState;

      depth++;
    }

    // Evaluate terminal state
    return this.evaluateTerminal(currentState, startingPlayer);
  }

  /**
   * Check if state is terminal
   */
  protected isTerminal(gameState: GameState): boolean {
    return (
      gameState.state === "Post Game" ||
      gameState.state === "Draw" ||
      gameState.state === "Terminated"
    );
  }

  /**
   * Evaluate terminal state
   */
  protected evaluateTerminal(gameState: GameState, startingPlayer: boolean): number {
    if (gameState.state === "Draw" || gameState.state === "Terminated") {
      return 0.5; // Draw
    }

    const lightWon = gameState.lightWon === true;
    const playerWon = lightWon === startingPlayer;

    return playerWon ? 1.0 : 0.0;
  }

  /**
   * Apply move to game state
   */
  protected applyMove(gameState: GameState, moveInfo: MoveInfo): GameState | null {
    try {
      const newState = this.deepCopyGameState(gameState);

      const fromSquare = newState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col];
      const toSquare = newState.board[moveInfo.move.position.row][moveInfo.move.position.col];

      // Find and move piece
      const pieceIndex = fromSquare.Pieces.findIndex((p) => p.id === moveInfo.move.pieceId);
      if (pieceIndex === -1) return null;

      const piece = fromSquare.Pieces.splice(pieceIndex, 1)[0];

      // Handle captures
      if (moveInfo.move.toPiece) {
        const captureIndex = toSquare.Pieces.findIndex((p) => p.id === moveInfo.move.toPiece?.id);
        if (captureIndex !== -1) {
          toSquare.Pieces.splice(captureIndex, 1);
        }
      }

      toSquare.Pieces.push(piece);

      // Check win conditions
      newState.isLightTurn = !newState.isLightTurn;

      // Check if king reached end
      if (piece.isKing) {
        if ((piece.isLight && moveInfo.move.position.row === 7) ||
            (!piece.isLight && moveInfo.move.position.row === 0)) {
          newState.state = "Post Game" as any;
          newState.lightWon = piece.isLight;
        }
      }

      return newState;
    } catch (error) {
      return null;
    }
  }

  /**
   * Deep copy game state. Uses cloneFullGameState so Piece prototypes survive
   * the round-trip — otherwise rollout pieces lose getValidMovesForPiece and
   * every MCTSNode.getAllMoves call inside a rollout silently throws and
   * returns no moves, making MCTS converge to 0.5 (draw) on everything.
   */
  protected deepCopyGameState(gameState: GameState): GameState {
    return cloneFullGameState(gameState);
  }

  /**
   * Heuristic-guided simulation (optional enhancement)
   * Slightly biases toward "good" moves for faster convergence
   */
  simulateWithHeuristics(gameState: GameState, startingPlayer: boolean): number {
    let currentState = this.deepCopyGameState(gameState);
    let depth = 0;

    while (!this.isTerminal(currentState) && depth < this.maxSimulationDepth) {
      const moves = MCTSNode.getAllMoves(currentState);

      if (moves.length === 0) {
        return 0.5;
      }

      // Score moves with simple heuristics
      const scoredMoves = moves.map((move) => ({
        move,
        score: this.scoreMove(currentState, move),
      }));

      // Sort by score
      scoredMoves.sort((a, b) => b.score - a.score);

      // Bias toward top moves (80% pick from top 30%, 20% random)
      const topIndex = Math.floor(scoredMoves.length * 0.3) || 1;
      const selectedMove =
        Math.random() < 0.8
          ? scoredMoves[Math.floor(Math.random() * topIndex)].move
          : scoredMoves[Math.floor(Math.random() * scoredMoves.length)].move;

      const nextState = this.applyMove(currentState, selectedMove);
      if (!nextState) {
        return 0.5;
      }
      currentState = nextState;

      depth++;
    }

    return this.evaluateTerminal(currentState, startingPlayer);
  }

  /**
   * Simple move scoring for heuristic simulations
   */
  protected scoreMove(gameState: GameState, moveInfo: MoveInfo): number {
    let score = 0;

    // Prefer captures
    if (moveInfo.move.toPiece) {
      score += 100;
    }

    // Prefer advancement
    const advancement = gameState.isLightTurn
      ? moveInfo.move.position.row - moveInfo.fromPosition.row
      : moveInfo.fromPosition.row - moveInfo.move.position.row;
    score += advancement * 10;

    // Prefer king advancement
    const piece = gameState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col].Pieces.find(
      (p) => p.id === moveInfo.move.pieceId
    );
    if (piece?.isKing) {
      score += advancement * 20;
    }

    // Add randomness
    score += Math.random() * 5;

    return score;
  }
}